/**
 * Deal Adjustments Panel (SPRINT-6B F008)
 *
 * CRUD panel for managing EBITDA normalisation adjustments at the deal level.
 * Supports addbacks (direction=1) and deductions (direction=-1) with
 * automatic ebitdaImpact calculation (amount * direction).
 *
 * States:
 * - Empty: "No EBITDA adjustments" with Add Addback / Add Deduction buttons
 * - Table: Columns Type | Description | Amount | Direction | Impact | Include | Actions
 * - Summary: Total Addbacks, Total Deductions, Net Impact
 * - Add Addback Modal (direction=1)
 * - Add Deduction Modal (direction=-1)
 * - Edit Modal: "Edit Adjustment"
 * - Delete AlertDialog: "Delete Adjustment"
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Loader2, Calculator } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

// Types
interface DealAdjustmentEntry {
  id: string;
  dealId: string;
  accountId: string | null;
  financialPeriodId: string | null;
  adjustmentType: string;
  description: string;
  amount: number;
  adjustmentDirection: number;
  ebitdaImpact: number;
  isRecurring: boolean;
  evidenceReference: string | null;
  notes: string | null;
  isIncluded: boolean;
  createdAt: string;
  updatedAt: string;
}

interface FormState {
  adjustmentType: string;
  description: string;
  amount: string;
  isRecurring: boolean;
  evidenceReference: string;
  notes: string;
  isIncluded: boolean;
}

export interface DealAdjustmentsPanelProps {
  dealId: string;
}

// Addback type options (direction=1)
const ADDBACK_TYPE_OPTIONS = [
  { value: 'addback_depreciation', label: 'Depreciation' },
  { value: 'addback_amortisation', label: 'Amortisation' },
  { value: 'addback_interest', label: 'Interest' },
  { value: 'addback_owner_salary', label: 'Owner Salary' },
  { value: 'addback_one_off_expense', label: 'One-off Expense' },
  { value: 'addback_related_party', label: 'Related Party' },
];

// Deduction type options (direction=-1)
const DEDUCTION_TYPE_OPTIONS = [
  { value: 'deduction_one_off_income', label: 'One-off Income' },
  { value: 'deduction_non_recurring', label: 'Non-recurring' },
  { value: 'deduction_related_party', label: 'Related Party' },
  { value: 'normalisation_rent', label: 'Rent Normalisation' },
  { value: 'normalisation_other', label: 'Other Normalisation' },
];

// Format currency
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

const defaultFormState: FormState = {
  adjustmentType: '',
  description: '',
  amount: '',
  isRecurring: false,
  evidenceReference: '',
  notes: '',
  isIncluded: true,
};

export const DealAdjustmentsPanel: React.FC<DealAdjustmentsPanelProps> = ({ dealId }) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Modal state: null = closed, 'addback' | 'deduction' = adding, 'edit' = editing
  const [modalMode, setModalMode] = useState<'addback' | 'deduction' | 'edit' | null>(null);
  const [editingEntry, setEditingEntry] = useState<DealAdjustmentEntry | null>(null);
  const [deleteEntry, setDeleteEntry] = useState<DealAdjustmentEntry | null>(null);
  const [formState, setFormState] = useState<FormState>(defaultFormState);

  // Fetch adjustments
  const { data: entries = [], isLoading } = useQuery<DealAdjustmentEntry[]>({
    queryKey: [`/api/crm/deals/${dealId}/adjustments`],
    enabled: !!dealId,
  });

  // Live calculation of impact
  const calculatedImpact = useMemo(() => {
    const amount = parseFloat(formState.amount) || 0;
    if (modalMode === 'edit' && editingEntry) {
      return amount * editingEntry.adjustmentDirection;
    }
    if (modalMode === 'addback') return amount * 1;
    if (modalMode === 'deduction') return amount * -1;
    return 0;
  }, [formState.amount, modalMode, editingEntry]);

  // Sync form state when editing
  useEffect(() => {
    if (editingEntry && modalMode === 'edit') {
      setFormState({
        adjustmentType: editingEntry.adjustmentType,
        description: editingEntry.description,
        amount: String(editingEntry.amount),
        isRecurring: editingEntry.isRecurring,
        evidenceReference: editingEntry.evidenceReference || '',
        notes: editingEntry.notes || '',
        isIncluded: editingEntry.isIncluded,
      });
    }
  }, [editingEntry, modalMode]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest('POST', `/api/crm/deals/${dealId}/adjustments`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/adjustments`] });
      handleCloseModal();
      toast({ title: 'Adjustment added', description: 'EBITDA adjustment has been created' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to create adjustment', variant: 'destructive' });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await apiRequest('PATCH', `/api/crm/deals/${dealId}/adjustments/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/adjustments`] });
      handleCloseModal();
      toast({ title: 'Adjustment updated', description: 'EBITDA adjustment has been updated' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to update adjustment', variant: 'destructive' });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest('DELETE', `/api/crm/deals/${dealId}/adjustments/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/adjustments`] });
      setDeleteEntry(null);
      toast({ title: 'Adjustment deleted', description: 'EBITDA adjustment has been removed' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to delete adjustment', variant: 'destructive' });
    },
  });

  // Toggle include mutation
  const toggleIncludeMutation = useMutation({
    mutationFn: async ({ id, isIncluded }: { id: string; isIncluded: boolean }) => {
      const res = await apiRequest('PATCH', `/api/crm/deals/${dealId}/adjustments/${id}`, { isIncluded });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/adjustments`] });
    },
  });

  const handleOpenAddback = () => {
    setEditingEntry(null);
    setFormState({ ...defaultFormState, adjustmentType: ADDBACK_TYPE_OPTIONS[0].value });
    setModalMode('addback');
  };

  const handleOpenDeduction = () => {
    setEditingEntry(null);
    setFormState({ ...defaultFormState, adjustmentType: DEDUCTION_TYPE_OPTIONS[0].value });
    setModalMode('deduction');
  };

  const handleOpenEdit = (entry: DealAdjustmentEntry) => {
    setEditingEntry(entry);
    setModalMode('edit');
  };

  const handleCloseModal = () => {
    setModalMode(null);
    setEditingEntry(null);
    setFormState(defaultFormState);
  };

  const handleSave = async () => {
    const amount = parseFloat(formState.amount);
    if (isNaN(amount) || amount <= 0) return;

    if (modalMode === 'edit' && editingEntry) {
      await updateMutation.mutateAsync({
        id: editingEntry.id,
        data: {
          adjustmentType: formState.adjustmentType,
          description: formState.description,
          amount,
          isRecurring: formState.isRecurring,
          evidenceReference: formState.evidenceReference || null,
          notes: formState.notes || null,
          isIncluded: formState.isIncluded,
        },
      });
    } else {
      const direction = modalMode === 'addback' ? 1 : -1;
      await createMutation.mutateAsync({
        adjustmentType: formState.adjustmentType,
        description: formState.description,
        amount,
        adjustmentDirection: direction,
        isRecurring: formState.isRecurring,
        evidenceReference: formState.evidenceReference || null,
        notes: formState.notes || null,
        isIncluded: formState.isIncluded,
      });
    }
  };

  // Calculate summary totals (only included entries)
  const summary = useMemo(() => {
    const included = entries.filter(e => e.isIncluded);
    const totalAddbacks = included
      .filter(e => e.adjustmentDirection === 1)
      .reduce((sum, e) => sum + Number(e.ebitdaImpact), 0);
    const totalDeductions = included
      .filter(e => e.adjustmentDirection === -1)
      .reduce((sum, e) => sum + Number(e.ebitdaImpact), 0);
    const netImpact = totalAddbacks + totalDeductions;
    return { totalAddbacks, totalDeductions, netImpact };
  }, [entries]);

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // Determine modal title and type options
  const modalTitle = modalMode === 'edit'
    ? 'Edit Adjustment'
    : modalMode === 'addback'
      ? 'Add Addback'
      : 'Add Deduction';

  const typeOptions = modalMode === 'edit'
    ? (editingEntry && editingEntry.adjustmentDirection === 1 ? ADDBACK_TYPE_OPTIONS : DEDUCTION_TYPE_OPTIONS)
    : modalMode === 'addback'
      ? ADDBACK_TYPE_OPTIONS
      : DEDUCTION_TYPE_OPTIONS;

  const submitLabel = modalMode === 'edit'
    ? 'Save'
    : modalMode === 'addback'
      ? 'Add Addback'
      : 'Add Deduction';

  // Loading state
  if (isLoading) {
    return (
      <Card data-testid="deal-adjustments-panel">
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="deal-adjustments-panel">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">EBITDA Adjustments</CardTitle>
            {entries.length > 0 && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleOpenAddback}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Addback
                </Button>
                <Button variant="outline" size="sm" onClick={handleOpenDeduction}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Deduction
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {entries.length === 0 ? (
            // Empty state
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Calculator className="h-8 w-8 text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground mb-2">No EBITDA adjustments</p>
              <p className="text-xs text-muted-foreground mb-6">
                Add normalisation adjustments to calculate adjusted EBITDA
              </p>
              <div className="flex gap-2">
                <Button onClick={handleOpenAddback}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Addback
                </Button>
                <Button variant="outline" onClick={handleOpenDeduction}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Deduction
                </Button>
              </div>
            </div>
          ) : (
            // Table view
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 font-medium text-muted-foreground">Type</th>
                      <th className="text-left py-2 px-2 font-medium text-muted-foreground">Description</th>
                      <th className="text-right py-2 px-2 font-medium text-muted-foreground">Amount</th>
                      <th className="text-center py-2 px-2 font-medium text-muted-foreground">Direction</th>
                      <th className="text-right py-2 px-2 font-medium text-muted-foreground">Impact</th>
                      <th className="text-center py-2 px-2 font-medium text-muted-foreground">Include</th>
                      <th className="text-right py-2 px-2 font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((entry) => (
                      <tr
                        key={entry.id}
                        className={`border-b ${!entry.isIncluded ? 'opacity-50' : ''}`}
                      >
                        <td className="py-2 px-2">
                          <Badge variant={entry.adjustmentDirection === 1 ? 'default' : 'destructive'}>
                            {entry.adjustmentDirection === 1 ? 'Addback' : 'Deduction'}
                          </Badge>
                        </td>
                        <td className="py-2 px-2">{entry.description}</td>
                        <td className="py-2 px-2 text-right">{formatCurrency(Number(entry.amount))}</td>
                        <td className="py-2 px-2 text-center">
                          {entry.adjustmentDirection === 1 ? '+' : '-'}
                        </td>
                        <td className="py-2 px-2 text-right" data-testid="ebitda-impact-display">
                          <span className={Number(entry.ebitdaImpact) >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {Number(entry.ebitdaImpact) >= 0 ? '+' : ''}{formatCurrency(Number(entry.ebitdaImpact))}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-center">
                          <Switch
                            checked={entry.isIncluded}
                            onCheckedChange={(checked) =>
                              toggleIncludeMutation.mutate({ id: entry.id, isIncluded: checked })
                            }
                          />
                        </td>
                        <td className="py-2 px-2 text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenEdit(entry)}
                              aria-label="edit"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteEntry(entry)}
                              aria-label="delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Summary section */}
              <div className="grid grid-cols-3 gap-4 pt-2 border-t">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Total Addbacks</p>
                  <p className="text-sm font-semibold text-green-600" data-testid="total-addbacks-value">
                    {formatCurrency(summary.totalAddbacks)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Total Deductions</p>
                  <p className="text-sm font-semibold text-red-600" data-testid="total-deductions-value">
                    {formatCurrency(summary.totalDeductions)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Net Impact</p>
                  <p
                    className={`text-sm font-semibold ${summary.netImpact >= 0 ? 'text-green-600' : 'text-red-600'}`}
                    data-testid="net-impact-value"
                  >
                    {summary.netImpact >= 0 ? '+' : ''}{formatCurrency(summary.netImpact)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={modalMode !== null} onOpenChange={(open) => !open && handleCloseModal()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{modalTitle}</DialogTitle>
            <DialogDescription>
              {modalMode === 'edit'
                ? 'Update the adjustment details below'
                : modalMode === 'addback'
                  ? 'Add an addback to increase adjusted EBITDA'
                  : 'Add a deduction to decrease adjusted EBITDA'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Adjustment Type select */}
            <div className="space-y-2">
              <Label htmlFor="adjustmentType">Adjustment Type</Label>
              <select
                id="adjustmentType"
                name="adjustmentType"
                value={formState.adjustmentType}
                onChange={(e) => setFormState(prev => ({ ...prev, adjustmentType: e.target.value }))}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                {typeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                name="description"
                data-testid="description-input"
                value={formState.description}
                onChange={(e) => setFormState(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g., Director salary addback"
              />
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label htmlFor="amount">Amount ($)</Label>
              <Input
                id="amount"
                name="amount"
                data-testid="amount-input"
                type="number"
                step="0.01"
                min="0"
                value={formState.amount}
                onChange={(e) => setFormState(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="e.g., 100000"
              />
            </div>

            {/* Calculated Impact (read-only) */}
            <div className="space-y-2">
              <Label>EBITDA Impact</Label>
              <div
                data-testid="modal-ebitda-impact"
                className={`flex h-9 w-full items-center rounded-md border border-input bg-muted px-3 py-2 text-sm font-medium ${
                  calculatedImpact >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {calculatedImpact >= 0 ? '+' : ''}{formatCurrency(calculatedImpact)}
              </div>
            </div>

            {/* Is Recurring */}
            <div className="flex items-center justify-between">
              <Label htmlFor="isRecurring">Recurring Adjustment</Label>
              <Switch
                id="isRecurring"
                data-testid="is-recurring-toggle"
                checked={formState.isRecurring}
                onCheckedChange={(checked) => setFormState(prev => ({ ...prev, isRecurring: checked }))}
              />
            </div>

            {/* Evidence Reference */}
            <div className="space-y-2">
              <Label htmlFor="evidenceReference">Evidence Reference</Label>
              <Input
                id="evidenceReference"
                name="evidenceReference"
                data-testid="evidence-input"
                value={formState.evidenceReference}
                onChange={(e) => setFormState(prev => ({ ...prev, evidenceReference: e.target.value }))}
                placeholder="e.g., FY24 P&L Note 3"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <textarea
                id="notes"
                name="notes"
                value={formState.notes}
                onChange={(e) => setFormState(prev => ({ ...prev, notes: e.target.value }))}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                placeholder="Additional notes..."
              />
            </div>

            {/* Include in Calculation */}
            <div className="flex items-center justify-between">
              <Label htmlFor="isIncluded">Include in Calculation</Label>
              <Switch
                id="isIncluded"
                checked={formState.isIncluded}
                onCheckedChange={(checked) => setFormState(prev => ({ ...prev, isIncluded: checked }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseModal} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                submitLabel
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteEntry} onOpenChange={(open) => !open && setDeleteEntry(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Adjustment</AlertDialogTitle>
            <AlertDialogDescription>
              Remove {deleteEntry?.description}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteEntry && deleteMutation.mutate(deleteEntry.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default DealAdjustmentsPanel;
